"use client";

import Image from "next/image";

import cn from "classnames";

import { Link } from "@/components/link/link.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { appendQueryString } from "@/utils/url.util";

import { brown } from "@/app/font";
import type { TwoColumnPromoSection as TwoColumnPromoSectionData } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

const promoRichTextBodyClassName = cn(
  "text-greyscale-body font-sans text-base font-normal leading-[130%]",
  "[font-feature-settings:'liga'_off,'clig'_off]",
  "[&_[data-testid='text'],&_ul,&_ol,&_li]:text-inherit",
  "[&_[data-testid='text'],&_ul,&_ol,&_li]:[font-family:inherit]",
  "[&_[data-testid='text'],&_ul,&_ol,&_li]:[font-feature-settings:inherit]",
  "[&_[data-testid='text'],&_ul,&_ol,&_li]:[font-size:inherit]",
  "[&_[data-testid='text'],&_ul,&_ol,&_li]:[font-style:inherit]",
  "[&_[data-testid='text'],&_ul,&_ol,&_li]:[font-weight:inherit]",
  "[&_[data-testid='text'],&_ul,&_ol,&_li]:[line-height:inherit]",
);

const promoLegalBodyClassName = cn(
  "text-greyscale-body font-sans text-base font-normal leading-[130%]",
  "[font-feature-settings:'liga'_off,'clig'_off]",
);

const promoLegalBodyMobileClassName = cn(
  "text-greyscale-body font-sans text-[12px] font-normal leading-[130%]",
  "[font-feature-settings:'liga'_off,'clig'_off]",
);

export const TwoColumnPromoSection = ({
  block,
}: {
  block: TwoColumnPromoSectionData | null | undefined;
}) => {
  if (!block?.headline || !block.body?.length || !block.mainImage) {
    return null;
  }

  const imageOnRight = block.imagePosition !== "left";
  const mainImageUrl = block.mainImage?.asset
    ? urlFor(block.mainImage).width(2400).height(1800).url()
    : null;

  const ctaHrefRaw = appendQueryString(
    block.ctaLink?.url,
    block.ctaLink?.queryString,
  );
  const ctaHref = ctaHrefRaw ?? "#";
  const showCta = Boolean(block.ctaLabel?.trim() && ctaHrefRaw);

  return (
    <section
      data-testid="two-column-promo-section"
      className={cn(
        "overflow-x-hidden bg-white px-4 py-0 max-sm:pt-10 lg:px-[163px] lg:py-16",
      )}>
      <div
        className={cn(
          "container mx-auto grid w-full grid-cols-1 items-stretch gap-0",
          "bg-primary-100 rounded-[20px] lg:grid-cols-2 lg:items-center lg:gap-[48px]",
        )}>
        <div
          className={cn(
            "order-2 flex max-w-full min-w-0 flex-col items-stretch text-left",
            "max-lg:px-6 max-lg:pt-[20px] max-lg:pb-10",
            "lg:max-w-176 lg:gap-6",
            imageOnRight ? "lg:order-1" : "lg:order-2",
          )}>
          {block.eyebrow ? (
            <div
              className={cn(
                brown.className,
                "text-greyscale-headline font-features-['liga'_off,'clig'_off] text-lg leading-[27px] font-bold uppercase max-lg:mb-2 lg:text-base lg:leading-6",
              )}>
              <RichText>{block.eyebrow}</RichText>
            </div>
          ) : null}

          <div
            className={cn(
              brown.className,
              "text-greyscale-headline font-features-['liga'_off,'clig'_off] text-[28px] leading-[115%] font-bold max-lg:mb-6 sm:text-[40px] lg:max-w-176",
            )}>
            <RichText
              blockComponents={{
                h2: ({ children }) => <Text component="h2">{children}</Text>,
              }}>
              {block.headline}
            </RichText>
          </div>

          <div className={cn(promoRichTextBodyClassName, "[&_a]:underline")}>
            <RichText>{block.body}</RichText>
          </div>
          {block.legalCopy ? (
            <div
              className={cn(
                promoLegalBodyClassName,
                "max-lg:hidden lg:mt-0 lg:hidden",
              )}>
              <RichText>{block.legalCopy}</RichText>
            </div>
          ) : null}

          {showCta ? (
            <div className="max-lg:mt-6 max-lg:flex max-lg:w-full max-lg:justify-center lg:pt-5 lg:pb-5">
              <Link
                href={ctaHref}
                secondary
                border
                className={cn(
                  "inline-flex px-8 py-3 uppercase",
                  "max-lg:w-full max-lg:justify-center",
                )}
                target={block.ctaLink?.openInNewTab ? "_blank" : undefined}
                rel={
                  block.ctaLink?.openInNewTab
                    ? "noopener noreferrer"
                    : undefined
                }
                data-testid="two-column-promo-cta">
                {block.ctaLabel}
              </Link>
            </div>
          ) : null}

          {block.legalCopy ? (
            <div className={cn(promoLegalBodyMobileClassName, "max-lg:mt-5")}>
              <RichText>{block.legalCopy}</RichText>
            </div>
          ) : null}
        </div>

        {mainImageUrl ? (
          <div
            className={cn(
              "relative order-1 aspect-4/3 min-h-[220px] w-full overflow-hidden rounded-none",
              "max-lg:ml-[calc(50%-50vw)] max-lg:w-screen max-lg:max-w-[100vw]",
              "lg:aspect-5/4 lg:min-h-[360px] lg:max-w-none lg:rounded-2xl",
              imageOnRight ? "lg:order-2" : "lg:order-1",
            )}>
            <Image
              src={mainImageUrl}
              alt={block.mainImage.alt ?? ""}
              fill
              className="object-cover"
              sizes="(max-width: 1023px) 100vw, 48vw"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};
